import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, Stack, TextField, Typography,
} from "@mui/material";

import { api, errorMessage, fieldErrors } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { BrandLogo } from "@/components/BrandMark";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { notify } from "@/store/toast";
import { useAuth, type AuthResult } from "@/store/auth";

/**
 * Holds the application back until an administrator-issued password is replaced.
 *
 * <p>The API enforces the same rule — every endpoint but this one refuses a token that still carries
 * the mark — so this screen is the way through, not the lock itself.</p>
 */
export function PasswordChangeGate({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);

  if (!user?.mustChangePassword) return <>{children}</>;

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
      <Card variant="outlined" sx={{ width: "100%", maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2.5}>
            <BrandLogo />
            <Box>
              <Typography variant="h6">Choose your own password</Typography>
              <Typography variant="body2" color="text.secondary">
                The password you signed in with was set for you. Replace it before you continue.
              </Typography>
            </Box>
            <ChangePasswordForm onDone={() => undefined} />
            <Button onClick={clear} sx={{ alignSelf: "flex-start" }}>Sign out</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

/** The same form, from the account menu, for anyone who wants to change theirs. */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open maxWidth="xs" fullWidth onClose={onClose}>
      <ClosableDialogTitle onClose={onClose}>Change password</ClosableDialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}><ChangePasswordForm onDone={onClose} /></Box>
      </DialogContent>
      <DialogActions />
    </Dialog>
  );
}

function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const user = useAuth((s) => s.user);
  const setSession = useAuth((s) => s.setSession);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const change = useMutation({
    mutationFn: async () => {
      await api.post(endpoints.users.changePassword, { currentPassword: current, newPassword: next });

      // A fresh sign-in returns a token without the must-change mark, and proves the new password works.
      const { data } = await api.post<AuthResult>(endpoints.auth.login, { email: user!.email, password: next });
      setSession(data);
    },
    onSuccess: () => { notify.success("Password changed"); onDone(); },
    onError: (error) => {
      const fields = fieldErrors(error);
      if (Object.keys(fields).length > 0) setErrors(fields);
      else notify.error(errorMessage(error));
    },
  });

  const mismatch = again.length > 0 && again !== next;

  return (
    <Stack
      component="form"
      spacing={2}
      onSubmit={(e) => { e.preventDefault(); setErrors({}); change.mutate(); }}
    >
      <TextField
        type="password" label="Current password" autoComplete="current-password" autoFocus
        value={current} onChange={(e) => setCurrent(e.target.value)}
        error={Boolean(errors.currentPassword)} helperText={errors.currentPassword}
      />
      <TextField
        type="password" label="New password" autoComplete="new-password"
        value={next} onChange={(e) => setNext(e.target.value)}
        error={Boolean(errors.newPassword)} helperText={errors.newPassword ?? "At least 8 characters, with a letter and a number."}
      />
      <TextField
        type="password" label="New password again" autoComplete="new-password"
        value={again} onChange={(e) => setAgain(e.target.value)}
        error={mismatch} helperText={mismatch ? "The two do not match." : undefined}
      />
      {change.isError && Object.keys(errors).length === 0 && <Alert severity="error">{errorMessage(change.error)}</Alert>}
      <Button type="submit" variant="contained" size="large" disabled={!current || !next || mismatch || change.isPending}>
        Change password
      </Button>
    </Stack>
  );
}
